import { defineStore } from 'pinia';
import { ref } from 'vue';
import router from '../router';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail, 
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification
} from 'firebase/auth';
import { useNotifications } from '../composables/useNotifications';
import { useProfileStore } from './profileStore';
import { auth } from '../config/firebase';
import { translateFirebaseError } from '../utils/errorTranslator';
import { setupTeacherAccount } from '../services/teacherService';

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const isLoading = ref(true);
  const role = ref(null);
  const emailVerified = ref(false);
  const { notifySuccess, notifyError } = useNotifications();
  const profileStore = useProfileStore();

  // --- 🔑 Auth Synchronization System ---
  let _resolveAuthReady;
  const authReady = new Promise((resolve) => {
    _resolveAuthReady = resolve;
  });

  auth.onAuthStateChanged(async (firebaseUser) => {
    console.log("[AuthStore] onAuthStateChanged triggered.");
    if (firebaseUser) {
      const token = await firebaseUser.getIdTokenResult();
      role.value = token.claims.role || 'teacher';
      user.value = firebaseUser;
      emailVerified.value = firebaseUser.emailVerified;
    } else {
      try {
        const result = await getRedirectResult(auth);
        if (result) notifySuccess('تم تسجيل الدخول بنجاح.');
      } catch (error) {
        console.error("[AuthStore] Redirect Login Error:", error);
        notifyError(translateFirebaseError(error.code));
      }
      user.value = null;
      role.value = null;
      emailVerified.value = false;
    }
    
    isLoading.value = false;
    _resolveAuthReady(); // 🔓 Signal that auth is stable
    console.log("[AuthStore] Auth is ready.");
  });

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      notifySuccess('تم تسجيل الدخول بنجاح.');
      return true; // Return success to component
    } catch (error) {
      notifyError(translateFirebaseError(error.code));
      return false;
    }
  };

  const register = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      notifySuccess('تم إنشاء الحساب! يرجى تفعيل البريد الإلكتروني.');
      return true;
    } catch (error) {
      notifyError(translateFirebaseError(error.code));
      return false;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      profileStore.clearProfile();
      notifySuccess('تم تسجيل الخروج بنجاح.');
      router.push({ name: 'Login' }); // Logout is an exception, it's a forced reset
    } catch (error) {
      notifyError(translateFirebaseError(error.code));
    }
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      notifySuccess('تم تسجيل الدخول بنجاح.');
      // Track registration only for brand-new users (first Google sign-in)
      if (result?.user?.metadata?.creationTime === result?.user?.metadata?.lastSignInTime) {
        const { trackCompleteRegistration } = await import('@/services/fbEvents');
        trackCompleteRegistration({ method: 'google' });
      }
      // أنشئ سجل DynamoDB + حدّث الـ role فور اكتمال الـ onboarding
      setupTeacherAccount({})
        .then(async () => {
          if (auth.currentUser) {
            const token = await auth.currentUser.getIdTokenResult(true); // force refresh
            role.value = token.claims.role || 'teacher';
          }
        })
        .catch(e => console.warn('[Auth] Onboarding call failed:', e));
      return true;
    } catch (error) {
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(auth, provider);
      } else {
        notifyError(translateFirebaseError(error.code));
      }
      return false;
    }
  };

  return { 
    user, isLoading, role, emailVerified, authReady,
    login, register, logout, forgotPassword: async (email) => {
      try {
        await sendPasswordResetEmail(auth, email);
        notifySuccess('تم إرسال رابط استعادة كلمة المرور.');
      } catch (e) { notifyError(translateFirebaseError(e.code)); }
    }, 
    loginWithGoogle, 
    forceUserUpdate: async () => {
      if (auth.currentUser) {
        await auth.currentUser.reload();
        user.value = auth.currentUser;
        emailVerified.value = auth.currentUser.emailVerified;
      }
    } 
  };
});
